/** XeAgents.ai**/
import { useTranslation } from "react-i18next";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Building2, GraduationCap, Home, ShieldCheck, type LucideIcon } from "lucide-react";
import { AuthStorage } from "@/lib/auth-storage";
import { useAuthDialog } from "@/components/landing/AuthDialog";

interface Sector {
  icon: LucideIcon;
  /** Key under sectors.* in the landing namespace. */
  key: string;
  /** Industry name on the Templates page / customize page (INDUSTRIES). */
  industry: string;
}

/**
 * Other sectors VoicePro runs in. Healthcare is the focus of the page, so these
 * are deliberately lighter than the healthcare workflow cards above. Each line
 * must describe a workflow the product already does (booking, reminders,
 * follow-ups, enquiries, handoff).
 */
export const OTHER_SECTORS: Sector[] = [
  { icon: ShieldCheck, key: "insurance", industry: "Insurance" },
  { icon: Building2, key: "realEstate", industry: "Real Estate" },
  { icon: GraduationCap, key: "education", industry: "Education & EdTech" },
  { icon: Home, key: "homeServices", industry: "All industries" },
];

export function SectorsStrip() {
  const { t } = useTranslation("landing");
  const shouldReduceMotion = useReducedMotion();
  const { openAuth } = useAuthDialog();

  // The Templates page is inside the app, so signed-out visitors register first.
  const openSector = (industry: string) => {
    if (AuthStorage.isAuthenticated()) {
      window.location.href = `/app/flows/templates?industry=${encodeURIComponent(industry)}`;
      return;
    }
    openAuth("register");
  };

  return (
    <div className="mx-auto max-w-7xl px-4 pb-12 sm:px-6 sm:pb-16 lg:px-8" data-testid="sectors-strip">
      <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-5 dark:border-slate-800 dark:bg-slate-900/40 sm:p-6">
        <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
            {t("sectors.eyebrow")}
          </p>
          <p className="text-sm text-slate-600 dark:text-slate-400">{t("sectors.note")}</p>
        </div>

        <ul className="-mx-1 mt-4 flex snap-x gap-3 overflow-x-auto px-1 pb-1 sm:mx-0 sm:grid sm:grid-cols-2 sm:overflow-visible sm:px-0 lg:grid-cols-4">
          {OTHER_SECTORS.map(({ icon: Icon, key, industry }, index) => (
            <motion.li
              key={key}
              initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              className="min-w-[14rem] flex-1 snap-start rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900 sm:min-w-0"
              data-testid={`sector-card-${key}`}
            >
              <div className="flex items-center gap-2.5">
                <Icon className="h-4 w-4 flex-shrink-0 text-slate-500 dark:text-slate-400" />
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
                  {t(`sectors.${key}.title`)}
                </h3>
              </div>
              <p className="mt-2 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                {t(`sectors.${key}.description`)}
              </p>
              <button
                type="button"
                onClick={() => openSector(industry)}
                className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-teal-700 hover:underline dark:text-teal-400"
                data-testid={`sector-cta-${key}`}
              >
                {t("sectors.cta")}
                <ArrowRight className="h-3 w-3" />
              </button>
            </motion.li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default SectorsStrip;
